import React, { useEffect, useState } from 'react';
import css from './VerifyPatient.module.css';
import {
  ErrorBanner,
  H2,
  HelplinePhoneNumber,
  IndicatesRequired,
  LoadingSpinner,
  LoadingSpinnerSize,
  NumberField,
  Paragraph,
  PhoneLink,
  StackedColumn,
  StackedColumnSpacing,
  TextLink,
} from '../../../../../core';

interface VerifyPatientProps {
  validate: boolean;
  contactInfo: {
    mrn: string;
    dateOfBirth: string;
    contactType: string;
    phoneKey: string;
    phoneString: string;
  };
  showError?: boolean;
  patientAccessCode?: string;
  sendAccessCode?: (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    phoneKey: string
  ) => Promise<boolean | undefined>;
  onChangePatientAccessCode: (patientAccessCode: string) => void;
  onTryAnotherMethod?: () => void;
  onError: () => void;
}

enum VerifyPatientState {
  SendContact = 'SendContact',
  Ready = 'Ready',
}

const errorContent = (
  <StackedColumn>
    <Paragraph text="We couldn't verify that entered code." />
    <div>
      <Paragraph text="Review the information to make sure it's correct, or contact the Marshfield Clinic Health System HelpLine at: " />
      <PhoneLink phoneNumber={HelplinePhoneNumber} includeImage={false} />
    </div>
  </StackedColumn>
);

const VerifyPatient: React.FC<VerifyPatientProps> = (
  props: VerifyPatientProps
) => {
  const [state, setState] = useState<VerifyPatientState>(
    VerifyPatientState.Ready
  );
  const [patientAccessCode, setPatientAccessCode] = useState<string>(
    props.patientAccessCode ?? ''
  );

  const sendContact = async () => {
    setState(VerifyPatientState.SendContact);
    if (props.sendAccessCode === undefined) {
      props.onError();
      return;
    }

    const result = await props.sendAccessCode(
      props.contactInfo.mrn,
      props.contactInfo.dateOfBirth,
      props.contactInfo.contactType,
      props.contactInfo.phoneKey
    );
    setState(VerifyPatientState.Ready);

    if (result === undefined || result === false) {
      props.onError();
      return;
    }
  };

  useEffect(() => {
    setPatientAccessCode(props.patientAccessCode ?? '');
  }, [props.patientAccessCode]);

  const onChangePatientAccessCode = (value: string) => {
    setPatientAccessCode(value);
    props.onChangePatientAccessCode(value);
  };

  const onNewCode = () => {
    sendContact();
  };

  return (
    <div className={css.container}>
      <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
        <H2 text="Please enter your verification code." />
        {state === VerifyPatientState.Ready ? (
          <>
            <IndicatesRequired />
            {props.showError ? (
              <ErrorBanner title="Please try again." content={errorContent} />
            ) : null}
            <div className={css.codeContainer}>
              <NumberField
                value={patientAccessCode}
                label="Enter your code"
                optionalText={`We've sent a message with your 6-digit verification code to: ${props.contactInfo.phoneString}`}
                required={true}
                validate={props.validate}
                onChange={onChangePatientAccessCode}
              />
            </div>
            <TextLink caption="Request new code" onClick={() => onNewCode()} />
            <TextLink
              caption="Try another method"
              onClick={() =>
                props.onTryAnotherMethod && props.onTryAnotherMethod()
              }
            />
          </>
        ) : (
          <LoadingSpinner
            label="Loading, please wait..."
            size={LoadingSpinnerSize.Medium}
          />
        )}
      </StackedColumn>
    </div>
  );
};

export default VerifyPatient;
